// Loads a saved route and runs the same client-side optimization the Route
// Planner uses (Google Directions optimizeWaypoints), then persists the order.
import { useState, useCallback, useEffect, useRef } from 'react';
import { getRoute, saveRouteOrder, updateRoute } from './routeService';
import { extractRouteInfo, generateGoogleMapsUrl } from '../leads/route-planner/routeUtils';

const MAX_WAYPOINTS = 23; // Google Directions limit (25 - origin - destination)

const toPoint = (label, lat, lng) =>
  (lat != null && lng != null) ? { label: label || '', lat: parseFloat(lat), lng: parseFloat(lng) } : null;

const useSavedRoute = (routeId, isGoogleLoaded) => {
  const [route, setRoute] = useState(null);
  const [leads, setLeads] = useState([]);       // ordered leads (each = lead + position)
  const [departure, setDeparture] = useState(null);
  const [destination, setDestination] = useState(null);
  const [isRoundTrip, setIsRoundTrip] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [routeResponse, setRouteResponse] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  const dirServiceRef = useRef(null);

  const applyRoute = useCallback((r) => {
    setRoute(r);
    // Each entry is a STOP, not a lead: a lead can hold several stops on one
    // route at different addresses. `stopId` is the identity everything
    // downstream keys off — lead ids are no longer unique within a route.
    //
    // Address fields come pre-resolved from the API (the stop's own address, or
    // the lead's primary when lead_address_id is null), so they deliberately
    // override the spread lead's street/city/lat/lng.
    const orderedLeads = (r.stops || [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(s => ({
        ...s.lead,
        stopId: s.id,
        leadId: s.lead?.id,
        position: s.position,
        leadAddressId: s.lead_address_id ?? null,
        addressLabel: s.addressLabel ?? null,
        isPrimaryAddress: s.isPrimary !== false,
        street: s.street,
        city: s.city,
        state: s.state,
        zipcode: s.zipcode,
        latitude: s.latitude,
        longitude: s.longitude,
      }));
    setLeads(orderedLeads);
    setDeparture(toPoint(r.departure_label, r.departure_lat, r.departure_lng));
    setDestination(toPoint(r.destination_label, r.destination_lat, r.destination_lng));
    setIsRoundTrip(r.is_round_trip !== false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getRoute(routeId);
      applyRoute(r);
    } catch (err) {
      setError('Failed to load route');
    } finally {
      setLoading(false);
    }
  }, [routeId, applyRoute]);

  useEffect(() => { load(); }, [load]);

  // Run a Directions request. optimize=true reorders waypoints (Generate); false
  // just draws the polyline for the current saved order.
  const runDirections = useCallback(async (orderedLeads, optimize) => {
    if (!isGoogleLoaded || !window.google?.maps) throw new Error('Maps not ready');
    if (!departure) throw new Error('Set a start location first.');

    const withCoords = orderedLeads.filter(l => l.latitude && l.longitude);
    if (!withCoords.length) throw new Error('Add leads with map coordinates first.');

    const capped = withCoords.slice(0, MAX_WAYPOINTS);
    const dest = (isRoundTrip || !destination) ? departure : destination;

    const waypoints = capped.map(l => ({
      location: new window.google.maps.LatLng(parseFloat(l.latitude), parseFloat(l.longitude)),
      stopover: true,
    }));

    if (!dirServiceRef.current) dirServiceRef.current = new window.google.maps.DirectionsService();

    const request = {
      origin: new window.google.maps.LatLng(departure.lat, departure.lng),
      destination: new window.google.maps.LatLng(dest.lat, dest.lng),
      waypoints,
      optimizeWaypoints: !!optimize,
      travelMode: window.google.maps.TravelMode.DRIVING,
    };

    const result = await new Promise((resolve, reject) => {
      dirServiceRef.current.route(request, (resp, status) => {
        if (status === 'OK') resolve(resp);
        else reject(new Error(`Could not build route (${status}).`));
      });
    });

    const order = optimize ? result.routes[0].waypoint_order : capped.map((_, i) => i);
    const ordered = order.map(i => capped[i]);
    return { result, ordered, dest };
  }, [isGoogleLoaded, departure, destination, isRoundTrip]);

  // Draw the polyline for the already-saved order (no persistence).
  const drawSavedOrder = useCallback(async () => {
    try {
      if (!departure || !leads.length) { setRouteResponse(null); setRouteInfo(null); return; }
      const { result, ordered, dest } = await runDirections(leads, false);
      setRouteResponse(result);
      setRouteInfo(extractRouteInfo(result));
      setGoogleMapsUrl(generateGoogleMapsUrl(departure, dest, ordered));
    } catch (err) {
      // Non-fatal: pins still render without a polyline.
      setRouteResponse(null);
    }
  }, [departure, leads, runDirections]);

  // Generate: optimize + persist new order + save dep/dest/round-trip.
  const generate = useCallback(async () => {
    setIsOptimizing(true);
    setGenerateError(null);
    try {
      const { result, ordered, dest } = await runDirections(leads, true);
      // Optimized stops first (in new order), then any uncoordinated stops appended.
      // Keyed by stopId: two stops of the same lead share a lead id, so matching
      // on that would drop the second one into "already ordered".
      const orderedIds = new Set(ordered.map(s => s.stopId));
      const leftover = leads.filter(s => !orderedIds.has(s.stopId));
      const finalOrder = [...ordered, ...leftover];

      await saveRouteOrder(routeId, finalOrder.map(s => s.stopId));
      await updateRoute(routeId, {
        is_round_trip: isRoundTrip,
        departure_label: departure?.label, departure_lat: departure?.lat, departure_lng: departure?.lng,
        destination_label: destination?.label, destination_lat: destination?.lat, destination_lng: destination?.lng,
      });

      setLeads(finalOrder);
      setRouteResponse(result);
      setRouteInfo(extractRouteInfo(result));
      setGoogleMapsUrl(generateGoogleMapsUrl(departure, dest, ordered));
    } catch (err) {
      setGenerateError(err.message || 'Could not generate the route.');
    } finally {
      setIsOptimizing(false);
    }
  }, [leads, runDirections, routeId, isRoundTrip, departure, destination]);

  // Persist departure/destination/round-trip without optimizing (e.g. on input change).
  const persistEndpoints = useCallback(async (patch) => {
    try { await updateRoute(routeId, patch); } catch (e) { /* best effort */ }
  }, [routeId]);

  return {
    route, leads, loading, error,
    departure, setDeparture, destination, setDestination, isRoundTrip, setIsRoundTrip,
    routeResponse, routeInfo, googleMapsUrl, isOptimizing, generateError,
    reload: load, generate, drawSavedOrder, persistEndpoints,
  };
};

export default useSavedRoute;
